import { m } from "#/paraglide/messages";
import {
  Badge,
  Button,
  createDataTableColumnHelper,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Link as UiLink,
  SearchField,
  Switch,
} from "@beweegbaas/ui";
import { createFileRoute, Link, stripSearchParams, useRouterState } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "@beweegbaas/ui";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { Pager } from "#/components/pager";
import { PendingContent } from "#/components/pending-content";
import {
  getActivityDetail,
  getSchoolActivitiesOverview,
} from "#/modules/beweegactiviteitenreeks/activities.functions";
import {
  ACTIVITIES_SEARCH_DEFAULTS,
  activitiesSearchSchema,
} from "#/modules/beweegactiviteitenreeks.overview/schema.search";
import { TogglePublishButton } from "#/modules/beweegactiviteitenreeks.overview/toggle-publish-button";
import { useOverviewSearch } from "#/modules/util.overview/use-overview-search";
import { useDownloadAllMediakaartenReeks } from "#/modules/mediakaarten/download-mediakaarten-reeks";
import { buildLocaleHref } from "#/modules/util.helpers/route-href";
import {
  getBeweegThemaTranslation,
  getLeerjaarComboTranslation,
  getLeerlijnTranslation,
} from "#/modules/util.i18n/enum-mapping";
import { activitiesOverviewQueryOptions } from "#/modules/beweegactiviteitenreeks.overview/query.activities-overview";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/activities/")({
  validateSearch: activitiesSearchSchema,
  // Keeps default filter/sort/pagination values out of the URL.
  search: { middlewares: [stripSearchParams(ACTIVITIES_SEARCH_DEFAULTS)] },
  // All search keys feed the query, so they belong in the match identity — entering with
  // different filters re-runs the loader (staleTime only skips same-deps re-entries).
  loaderDeps: ({ search }) => search,
  staleTime: Infinity,
  // Primes the same query the component reads; loader data doubles as the render fallback
  // when the query cache entry was GC'd.
  loader: ({ context, params: { school, schoolyear }, deps }) =>
    context.queryClient.ensureQueryData(
      activitiesOverviewQueryOptions({ school, schoolyear }, deps),
    ),
  component: SchoolActivitiesPage,
  errorComponent: ActivitiesError,
});

function ActivitiesError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

type ActivityRow = Awaited<
  ReturnType<typeof getSchoolActivitiesOverview>
>["beweegactiviteitenreeksen"][number];

// Full-width leerlijn group headers are emulated as marker rows (DataTable has no colspan rows).
type ActivitiesTableRow =
  | { kind: "group"; leerlijn: ActivityRow["leerlijn"] }
  | { kind: "activity"; activity: ActivityRow };

const helper = createDataTableColumnHelper<ActivitiesTableRow>();

function SchoolActivitiesPage() {
  const loaderData = Route.useLoaderData();
  const { locale, schoolyear, school } = Route.useParams();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { searchTerm, includeDefaults } = search;
  const overview = useOverviewSearch(search, navigate);

  // Data reads live in TanStack Query: the query key is the typed, match-synced `Route.useSearch()`
  // value, so filter/sort/page changes are served by this query (keepPreviousData → stale rows stay
  // visible + dimmed while the next result fetches), NOT by re-running the route loader. The loader
  // already primed this exact key, so the cache entry carries a truthful `dataUpdatedAt` and goes
  // stale on schedule; `loaderData` is only the render fallback if that entry was GC'd (in which
  // case this query is refetching, and `isListPending` dims the rows).
  const overviewQuery = useQuery({
    ...activitiesOverviewQueryOptions({ school, schoolyear }, search),
    placeholderData: keepPreviousData,
  });
  const { beweegactiviteitenreeksen, totalPages, totalCount } = overviewQuery.data ?? loaderData;

  // During a deps-change transition the mounted query (old key) isn't fetching, so the dim
  // needs the router's pending state too.
  const isNavigating = useRouterState({ select: (s) => s.isLoading });
  const isListPending = overviewQuery.isFetching || isNavigating;

  const [downloadingMediakaartenReeksId, setDownloadingMediakaartenReeksId] = useState<
    string | null
  >(null);
  const downloadAllMediakaartenMutation = useDownloadAllMediakaartenReeks();
  const getActivityDetailFn = useServerFn(getActivityDetail);

  const handleDownloadMediakaarten = async (rowId: string) => {
    setDownloadingMediakaartenReeksId(rowId);
    try {
      const data = await getActivityDetailFn({ data: { id: rowId } });
      if (!data) {
        setDownloadingMediakaartenReeksId(null);
        return;
      }
      const resourceBaseUrl = `${window.location.origin}${buildLocaleHref(
        `/api/${school}/resource`,
        { locale, schoolyear },
      )}`;
      downloadAllMediakaartenMutation.mutate(
        { reeks: data.beweegactiviteitenreeks, resourceBaseUrl },
        { onSettled: () => setDownloadingMediakaartenReeksId(null) },
      );
    } catch {
      toast.error(m["mediakaarten.download_failed"]());
      setDownloadingMediakaartenReeksId(null);
    }
  };

  const rows = useMemo<ActivitiesTableRow[]>(() => {
    const groups = new Map<ActivityRow["leerlijn"], ActivityRow[]>();
    for (const activity of beweegactiviteitenreeksen) {
      const group = groups.get(activity.leerlijn) ?? [];
      group.push(activity);
      groups.set(activity.leerlijn, group);
    }
    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([leerlijn, activities]) => [
        { kind: "group" as const, leerlijn },
        ...activities.map((activity) => ({ kind: "activity" as const, activity })),
      ]);
  }, [beweegactiviteitenreeksen]);

  const columns = useMemo(
    () =>
      helper.columns([
        helper.display({
          id: "name",
          header: () => m["common.fields.name"](),
          enableSorting: true,
          meta: { rowTarget: true },
          cell: ({ row }) => {
            if (row.original.kind === "group") return getLeerlijnTranslation(row.original.leerlijn);
            const { activity } = row.original;
            return (
              <UiLink
                variant="secondary"
                underline
                to="/$locale/{-$schoolyear}/$school/activities/$id/edit"
                params={(prev: Record<string, string>) => ({ ...prev, id: activity.id })}
              >
                {activity.name}
              </UiLink>
            );
          },
        }),
        helper.display({
          id: "leerlijn",
          header: () => m["beweegactiviteitenreeks.table.leerlijn"](),
          cell: ({ row }) =>
            row.original.kind === "activity"
              ? getLeerlijnTranslation(row.original.activity.leerlijn)
              : null,
        }),
        helper.display({
          id: "beweegthema",
          header: () => m["beweegactiviteitenreeks.table.beweegthema"](),
          cell: ({ row }) =>
            row.original.kind === "activity"
              ? getBeweegThemaTranslation(row.original.activity.beweegthema)
              : null,
        }),
        helper.display({
          id: "classGradeGrouping",
          header: () => m["common.leerjaar"](),
          cell: ({ row }) =>
            row.original.kind === "activity" ? (
              <div className="flex flex-wrap gap-2">
                {row.original.activity.leerjaarCombo
                  .map((leerjaarCombo) => getLeerjaarComboTranslation(leerjaarCombo))
                  .join(", ")}
              </div>
            ) : null,
        }),
        helper.display({
          id: "isDefault",
          header: "",
          meta: { cellClassName: "w-0" },
          cell: ({ row }) =>
            row.original.kind === "activity" ? (
              row.original.activity.isDefault ? (
                <Badge variant="gray" size="sm">
                  Beweegbaas
                </Badge>
              ) : (
                <Badge size="sm" variant="blue">
                  Eigen activiteit
                </Badge>
              )
            ) : null,
        }),
        helper.display({
          id: "actions",
          header: "",
          meta: { action: true, headerClassName: "w-0" },
          cell: ({ row }) => {
            if (row.original.kind !== "activity") return null;
            const activity = row.original.activity;
            return (
              <>
                {activity.canTogglePublished && (
                  <TogglePublishButton reeksId={activity.id} isPublished={activity.isPublished} />
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={<Button variant="secondary" size="icon" aria-label="Acties" />}
                  >
                    <IconMdiDotsVertical className="size-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      render={
                        <Link
                          from={Route.fullPath}
                          to="/$locale/{-$schoolyear}/$school/activities/mediakaart-preview/$reeksId"
                          params={(prev: Record<string, string>) => ({
                            ...prev,
                            reeksId: activity.id,
                          })}
                        />
                      }
                      nativeButton={false}
                    >
                      <IconMaterialSymbolsVisibilityOutline className="size-4" />
                      Preview mediakaarten
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={downloadingMediakaartenReeksId === activity.id}
                      onClick={(e) => {
                        e.preventDefault();
                        void handleDownloadMediakaarten(activity.id);
                      }}
                    >
                      <IconMaterialSymbolsDownload className="size-4" />
                      Download mediakaarten
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            );
          },
        }),
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [downloadingMediakaartenReeksId],
  );

  return (
    <Page>
      <Page.Header>
        <Page.Header.SearchField>
          <SearchField
            placeholder="Zoek op naam"
            className={"w-auto grow md:w-xs"}
            {...overview.searchFieldProps}
          />
        </Page.Header.SearchField>

        <Page.Header.FilterItems className="hidden items-center gap-2 md:flex">
          <Switch
            id="showBeweegbaasThemes"
            name={"showBeweegbaasThemes"}
            checked={includeDefaults}
            onCheckedChange={(checked) => {
              void navigate({
                search: (prev) => ({ ...prev, includeDefaults: checked, page: 1 }),
                replace: true,
                resetScroll: false,
              });
            }}
          />
          <label htmlFor="showBeweegbaasThemes" className="text-sm">
            Toon Beweegbaas activiteiten
          </label>
        </Page.Header.FilterItems>

        <Page.Header.Actions>
          <Page.Header.MainAction>
            <Button
              className="sm:ml-auto"
              size="xl"
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/activities/add"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              nativeButton={false}
            >
              {m["common.actions.add"]()}
              <IconMaterialSymbolsAdd />
            </Button>
          </Page.Header.MainAction>
        </Page.Header.Actions>
      </Page.Header>
      <PendingContent pending={isListPending}>
        <DataTable
          alignToContainer
          aria-label="Beweegactiviteitenreeksen"
          columns={columns}
          data={rows}
          getRowId={(row) => (row.kind === "group" ? `group-${row.leerlijn}` : row.activity.id)}
          empty={
            !searchTerm && !isListPending ? m["beweegactiviteitenreeks.leeg_eigen"]() : undefined
          }
          getRowClassName={(row) =>
            row.kind === "group"
              ? "bg-muted hover:bg-muted font-bold"
              : !row.activity.isPublished
                ? "text-muted-foreground line-through"
                : undefined
          }
          {...overview.sortProps}
        />

        <Page.Container>
          <Pager
            totalPages={totalPages}
            totalCount={totalCount}
            className={"border-0"}
            {...overview.pagerProps}
          />
        </Page.Container>
      </PendingContent>
    </Page>
  );
}
